import React, { useId } from "react";
import { Check, Eye, Pencil, SlidersHorizontal } from "lucide-react";
import type { AgentConnection } from "../../../types";

/**
 * The three choices, from the most careful to the widest, read left to right
 * like the plans on a product page. Each one lists only what it adds to the
 * one before it, so the difference between two choices is its own list.
 */
const AGENT_SCOPE_TIERS: {
  value: AgentConnection["scope"];
  label: string;
  icon: React.ReactNode;
  adds: string[];
  note?: string;
}[] = [
  {
    value: "read",
    label: "Look only",
    icon: <Eye size={15} />,
    adds: [
      "See your tasks and their details",
      "Open the files on a task",
      "Read the thoughts waiting in Captures",
      "Read the mail in a mailbox you connected",
      "Read your Agent instructions",
    ],
  },
  {
    value: "read_write",
    label: "Add and change work",
    icon: <Pencil size={15} />,
    adds: [
      "Create tasks and write down details",
      "Mark tasks done and archive them",
      "Turn a task into a subtask, and back",
      "Attach and rename files",
      "Rename a list you own",
      "Keep an email as a task or a subtask, which archives it",
      "Turn a captured thought into a task, once you say so",
      "Change your Agent instructions, once you say yes",
      "Create, rename and recolour tags",
    ],
  },
  {
    value: "read_write_delete",
    label: "Full control",
    icon: <SlidersHorizontal size={15} />,
    adds: [
      "Delete a task or a subtask",
      "Delete a file or a link",
      "Clear a captured thought",
      "Delete a tag",
    ],
    note: "It names what will go and deletes only after you say yes. A deleted item cannot be brought back.",
  },
];

const AgentScopePicker: React.FC<{
  name: string;
  value: AgentConnection["scope"];
  onChange: (scope: AgentConnection["scope"]) => void;
}> = ({ name, value, onChange }) => {
  const id = useId();

  return (
    <fieldset>
      <legend className="text-xs text-warm-text-secondary mb-1.5">
        What it may do
      </legend>
      <div className="grid gap-2 sm:grid-cols-3">
        {AGENT_SCOPE_TIERS.map((tier, index) => {
          const selected = value === tier.value;
          const previous = AGENT_SCOPE_TIERS[index - 1];
          const detailsId = `${id}-${tier.value}`;
          return (
            <div
              key={tier.value}
              className={`relative flex flex-col p-3 rounded-soft border transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-warm-blue ${
                selected
                  ? "border-warm-blue bg-warm-bg-elevated"
                  : "border-warm-border hover:bg-warm-bg-elevated"
              }`}
            >
              {/* The label stretches over the whole card, so a click anywhere
                  picks it, and the list below can stay a real list. */}
              <label className="flex items-center gap-2 cursor-pointer after:absolute after:inset-0 after:rounded-soft">
                <input
                  type="radio"
                  name={name}
                  value={tier.value}
                  checked={selected}
                  onChange={() => onChange(tier.value)}
                  aria-describedby={detailsId}
                  className="sr-only"
                />
                <span className="text-warm-text-secondary">{tier.icon}</span>
                <span className="text-sm font-medium text-warm-text-primary">
                  {tier.label}
                </span>
                <span
                  aria-hidden="true"
                  className={`ml-auto w-4 h-4 flex-shrink-0 rounded-full border transition-all ${
                    selected
                      ? "border-[5px] border-warm-blue"
                      : "border-warm-border"
                  }`}
                />
              </label>

              <div id={detailsId} className="mt-3">
                {previous && (
                  <p className="mb-1.5 text-xs font-medium text-warm-text-secondary">
                    Everything in {previous.label}, plus:
                  </p>
                )}
                <ul className="space-y-1.5">
                  {tier.adds.map((line) => (
                    <li
                      key={line}
                      className="flex items-start gap-1.5 text-xs text-warm-text-secondary"
                    >
                      <Check
                        size={13}
                        aria-hidden="true"
                        className="mt-px flex-shrink-0 text-warm-blue"
                      />
                      {line}
                    </li>
                  ))}
                </ul>
                {tier.note && (
                  <p className="mt-3 text-xs text-warm-text-tertiary">
                    {tier.note}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
};

export default AgentScopePicker;
